import type { ReactNode } from "react";

import {
  type AuthorizationParamsLike,
  useInitialLocale,
} from "@/hooks/useInitialLocale";

import { CustomerAccountTitle, PageLayout } from "./PageLayout";

interface ResetPasswordScreenShellProps {
  /**
   * Locale parameters from Auth0 context or the success-screen URL fallback.
   * Used to resolve the initial locale (including CH language switching).
   */
  authorizationParams: AuthorizationParamsLike | null | undefined;
  showCustomerAccountTitle?: boolean;
  /**
   * `column` adds the shared form container; `hero` lets the screen
   * provide its own layout.
   */
  contentLayout?: "column" | "hero";
  /** Screen body (subtitle, form, etc.). */
  children?: ReactNode;
  /** Full-width notice between main content and the footer. */
  banner?: ReactNode;
}

/**
 * Shared password-reset page: header/footer, locale, and the max-width
 * content column, without login/signup tabs.
 *
 * The Kundenkonto title is opt-in per screen via {@link showCustomerAccountTitle}.
 */
export function ResetPasswordScreenShell({
  authorizationParams,
  showCustomerAccountTitle = false,
  contentLayout = "column",
  children,
  banner,
}: ResetPasswordScreenShellProps) {
  const initialLocale = useInitialLocale(authorizationParams);

  if (!initialLocale) {
    return null;
  }

  return (
    <PageLayout initialLocale={initialLocale} banner={banner}>
      {contentLayout === "hero" ? (
        children
      ) : (
        <div className="flex w-[calc(100%-32px)] max-w-[808px] flex-col gap-1 py-1 sm:w-[calc(100%-48px)] sm:gap-1.5 sm:py-3 md:py-4">
          {showCustomerAccountTitle ? <CustomerAccountTitle /> : null}
          {children}
        </div>
      )}
    </PageLayout>
  );
}
